import { getUnixTime } from 'date-fns/getUnixTime';
import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import { camelcaseKeys } from 'src/utils/camelize-keys';
import { decamelize } from 'src/utils/decamelize';

import { Client, ClientFile, EmailType } from './_types';
import { denormalizeClient, normalizeClient } from './normalizers';

export * from './_types';

interface FetchClientsOptions {
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  page?: number;
  perPage?: number;
  search?: string;
  ids?: Identifier[];
  filters?: {
    trainerId?: string;
    status?: string;
    group?: string;
    workoutPlan?: string;
    tags?: string[];
    matchAllTags?: boolean;
    customStatus?: string;
    lifecycleStage?: number[];
    appPlatform?: string;
    customProfileField?: {
      field: string;
      value: string;
    };
  };
}
export const fetchClientsQueryKey = (o: FetchClientsOptions) => [
  'clients',
  o.sortBy,
  o.sortDir,
  o.page,
  o.perPage,
  o.search,
  o.filters,
];

export const statusParams = (status?: string) => {
  if (!status) return {};

  switch (status.toLowerCase()) {
    case 'pending':
      return { 'q[has_status]': 'pending' };
    case 'frozen':
      return { 'q[has_status]': 'paused' };
    case 'inactive':
      return { 'q[has_status]': 'inactive' };
    case 'pending cancellation':
      return { 'q[has_status]': 'pending_cancel' };
    case 'billed':
      return { 'q[has_subscriptions]': 1 };
    case 'paid':
      return { 'q[only_purchase]': 1 };
    case 'non-billed':
      return {
        'q[no_subscriptions]': 1,
        'q[no_purchases]': 1,
        'q[status_not_eq]': 'pending',
      };
    default:
      return {};
  }
};

export const fetchClients = async ({
  sortBy,
  sortDir,
  page = 0,
  perPage = 20,
  search = '',
  ids,
  filters = {},
}: FetchClientsOptions) => {
  const response = await api.get(`/api/v2/clients/`, {
    params: {
      page,
      per: perPage,
      ...(search && { 'q[client_search]': search }),
      ...(ids && { with_ids: ids.join(',') }),
      ...(sortBy && {
        'q[s]': `${decamelize(sortBy || '')} ${sortDir || 'desc'}`,
      }),
      ...(filters.trainerId && { 'q[has_trainer]': filters.trainerId }),
      ...statusParams(filters.status), // Use the converted status parameters
      ...(filters.group && { 'q[client_filter_group]': filters.group }),
      ...(filters.workoutPlan && { 'q[filter_plan]': filters.workoutPlan }),
      ...(filters.lifecycleStage && {
        'q[has_lifecycle_stage]': filters.lifecycleStage,
      }),
      ...(filters.customStatus && {
        'q[client_filter]': filters.customStatus,
      }),
      // Without Match all tags
      ...(!filters.matchAllTags &&
        filters.tags && {
          'q[client_tags_any]': filters.tags.join(','),
        }),
      // With Match all tags
      ...(filters.matchAllTags &&
        filters.tags && { 'q[client_tags]': filters.tags.join(',') }),
      ...(filters.customProfileField && {
        'q[client_profile_field]': `${filters.customProfileField.field}::${filters.customProfileField.value}`,
      }),
      ...(filters.appPlatform && {
        'q[app_platform]': filters.appPlatform,
      }),
    },
  });

  const normalizedClients = response.data?.client?.map((client) =>
    normalizeClient(client)
  );

  return { clients: normalizedClients, total: response.data?.meta?.total };
};

interface FetchClientOptions {
  id: Client['id'];
}

export const fetchClientQueryKey = (o: FetchClientOptions) => ['client', o.id];

export const fetchClient = async ({
  id,
}: FetchClientOptions): Promise<Client> => {
  const response = await api.get(`/api/v2/clients/${id}`, {});

  const normalizedClient = normalizeClient(response.data);

  return normalizedClient;
};

export const createClient = async ({ client }) => {
  const data = denormalizeClient(client);
  const response = await api.post(`/api/v2/clients/`, { client: data });
  return normalizeClient(response.data);
};

export const bulkInvite = async ({ client }) => {
  const data = denormalizeClient(client);
  const response = await api.get(`/api/v3/clients/bulk_invite`, {
    params: {
      ...data,
      welcomeMessage: client.welcomeMessage,
    },
  });
  return normalizeClient(response.data);
};

export const updateClient = async ({ clientId, client }) => {
  const data = denormalizeClient(client);
  const response = await api.put(
    `/api/v2/clients/${clientId}`,
    { client: data },
    {}
  );
  return normalizeClient(response.data);
};

export const updateClientMeasurementNotes = async ({
  clientId,
  measurementNotes,
}: {
  clientId: string;
  measurementNotes: string;
}): Promise<Client> => {
  const response = await api.put(
    `/api/v2/clients/${clientId}`,
    { client: { measurement_notes: measurementNotes } },
    {}
  );
  return normalizeClient(response.data);
};

export const addClientFile = async ({
  clientId,
  file,
}: {
  clientId: string;
  file: string;
}): Promise<ClientFile> => {
  const response = await api.post(`/api/v2/clients/${clientId}/add_file/`, {
    file: { signed_id: file },
  });
  // Assuming the API returns the newly created file information
  return camelcaseKeys(response.data);
};

export const deleteClientFile = async ({
  clientId,
  file,
}: {
  clientId: Client['id'];
  file: ClientFile;
}): Promise<void> => {
  await api.post(`/api/v2/clients/${clientId}/remove_file/`, {
    file_id: file.id,
    type: file.type,
  });
};

interface SendBillingEmailParams {
  clientId: Identifier;
  planId?: string;
}

export const sendBillingEmail = async ({
  clientId,
  planId,
}: SendBillingEmailParams): Promise<void> => {
  await api.post(`/api/v2/clients/${clientId}/resend_invite/`, {
    billed: planId ? true : undefined,
    plan_id: planId,
  });
};

interface BulkUpdateParams {
  clientIds: Client['id'][];
  destroy?: boolean;
  deleteAccount?: boolean;
  invite?: boolean;
  emailType?: EmailType;
  mergeInto?: Client['id'];
  selectAll?: boolean;
  filters?: {
    trainerId?: string;
    status?: string;
    group?: string;
    workoutPlan?: string;
    tags?: {
      values: string[];
      matchAll?: boolean;
    };
    customStatus?: string;
    lifecycleStage?: number[];
    appPlatform?: string;
    customProfileField?: {
      field: string;
      value: string;
    };
  };
  // New parameters for manage dialog
  trainerId?: string | null;
  resourceIds?: string[];
  videoIds?: string[];
  groupId?: string | null;
  weekPlanId?: string | null;
  notifyPlan?: boolean;
  assessmentId?: string | null;
  sequenceId?: string | null;
  resourceMessage?: string | null;
  videoMessage?: string | null;
  startedAt?: string | null;
  locationId?: string | null;
  lifecycleStageId?: string | null;
  timeZone?: string | null;
  status?: string | null;
  assessmentReminder?: boolean;
  sendWelcomeEmail?: boolean;
}

export const bulkUpdateClients = async ({
  clientIds,
  destroy,
  deleteAccount,
  invite,
  emailType,
  mergeInto,
  selectAll,
  filters,
  trainerId,
  resourceIds,
  videoIds,
  groupId,
  weekPlanId,
  notifyPlan,
  assessmentId,
  sequenceId,
  resourceMessage,
  videoMessage,
  startedAt,
  locationId,
  lifecycleStageId,
  timeZone,
  status,
  assessmentReminder,
  sendWelcomeEmail,
}: BulkUpdateParams): Promise<void> => {
  await api.get('/api/v3/clients/bulk_update', {
    params: {
      client_ids: clientIds,
      ...(destroy && { destroy: 1 }),
      ...(deleteAccount && { delete_account: 1 }),
      ...(invite && { invite: true }),
      ...(emailType && { email_type: emailType }),
      ...(mergeInto && { merge_into: mergeInto }),
      ...(selectAll && {
        all_pages: true,
        // Include current filters when selecting all pages
        ...(filters && {
          'q[has_trainer]': filters.trainerId,
          ...statusParams(filters.status),
          'q[client_filter_group]': filters.group,
          'q[filter_plan]': filters.workoutPlan,
          'q[has_lifecycle_stage]': filters.lifecycleStage,
          'q[client_filter]': filters.customStatus,
          ...(!filters.tags?.matchAll &&
            filters.tags?.values && {
              'q[client_tags_any]': filters.tags.values.join(','),
            }),
          ...(filters.tags?.matchAll &&
            filters.tags?.values && {
              'q[client_tags]': filters.tags.values.join(','),
            }),
          ...(filters.customProfileField && {
            'q[client_profile_field]': `${filters.customProfileField.field}::${filters.customProfileField.value}`,
          }),
          ...(filters.appPlatform && {
            'q[app_platform]': filters.appPlatform,
          }),
        }),
      }),
      // New manage dialog parameters
      ...(trainerId !== undefined && { sub_trainer_id: trainerId }),
      ...(resourceIds?.length && { resource_ids: resourceIds.join(',') }),
      ...(videoIds?.length && { video_ids: videoIds.join(',') }),
      ...(groupId !== undefined && { group_to_join: groupId }),
      ...(weekPlanId !== undefined && { week_plan: weekPlanId }),
      ...(weekPlanId &&
        notifyPlan !== undefined && { notify_plan: notifyPlan }),
      ...(assessmentId !== undefined && { assessment_id: assessmentId }),
      ...(sequenceId !== undefined && { sequence_id: sequenceId }),
      ...(resourceMessage !== undefined && { message: resourceMessage }),
      ...(videoMessage !== undefined && { video_message: videoMessage }),
      ...(startedAt && {
        started_at: getUnixTime(new Date(startedAt)),
      }),
      ...(locationId !== undefined && { location_id: locationId }),
      ...(lifecycleStageId !== undefined && {
        lifecycle_stage_id: lifecycleStageId,
      }),
      ...(timeZone !== undefined && { time_zone: timeZone }),
      ...(status !== undefined && { status }),
      ...(assessmentReminder && { assessment_reminder: Date.now() / 1000 }),
      ...(sendWelcomeEmail !== undefined && {
        send_welcome_email: sendWelcomeEmail,
      }),
    },
  });
};

interface BulkSendMessageParams {
  userIds: Identifier[];
  message: string;
  delivery: string[];
  selectAll?: boolean;
  search?: string;
  filters?: {
    trainerId?: string;
    status?: string;
    group?: string;
    workoutPlan?: string;
    tags?: {
      values: string[];
      matchAll?: boolean;
    };
    customStatus?: string;
    lifecycleStage?: number[];
    appPlatform?: string;
    customProfileField?: {
      field: string;
      value: string;
    };
  };
}

export const bulkSendMessage = async ({
  userIds,
  message,
  delivery,
  selectAll,
  search,
  filters,
}: BulkSendMessageParams): Promise<void> => {
  await api.post('/api/v3/clients/bulk_send', {
    user_ids: userIds.map((id) => id.toString()),
    message: { body: message, delivery },
    ...(selectAll && {
      all_pages: true,
      ...(search && { 'q[client_search]': search.trim() }),
      ...(filters && {
        'q[has_trainer]': filters.trainerId,
        ...statusParams(filters.status),
        'q[client_filter_group]': filters.group,
        'q[filter_plan]': filters.workoutPlan,
        'q[has_lifecycle_stage]': filters.lifecycleStage,
        'q[client_filter]': filters.customStatus,
        ...(!filters.tags?.matchAll &&
          filters.tags?.values && {
            'q[client_tags_any]': filters.tags.values.join(','),
          }),
        ...(filters.tags?.matchAll &&
          filters.tags?.values && {
            'q[client_tags]': filters.tags.values.join(','),
          }),
        ...(filters.customProfileField && {
          'q[client_profile_field]': `${filters.customProfileField.field}::${filters.customProfileField.value}`,
        }),
        ...(filters.appPlatform && {
          'q[app_platform]': filters.appPlatform,
        }),
      }),
    }),
  });
};

interface ExportClientInfoParams {
  clientIds?: string;
  selectAll?: boolean;
  search?: string;
  filters?: {
    trainer?: string;
    status?: string;
    group?: string;
    workoutPlan?: string;
    tags?: {
      values: string[];
      matchAll?: boolean;
    };
    customStatus?: string;
    lifecycleStage?: number[];
    appPlatform?: string;
    customProfileField?: {
      field: string;
      value: string;
    };
  };
}

export const exportClientInfo = async ({
  clientIds,
  selectAll,
  search,
  filters,
}: ExportClientInfoParams) => {
  return api.get('/api/v2/clients/export_info', {
    params: {
      ...(clientIds && { client_ids: clientIds }),
      ...(selectAll && {
        fetch_all: true,
        ...(search && { 'q[client_search]': search.trim() }),
        ...(filters && {
          'q[has_trainer]': filters.trainer,
          ...statusParams(filters.status),
          'q[client_filter_group]': filters.group,
          'q[filter_plan]': filters.workoutPlan,
          'q[has_lifecycle_stage]': filters.lifecycleStage,
          'q[client_filter]': filters.customStatus,
          ...(!filters.tags?.matchAll &&
            filters.tags?.values && {
              'q[client_tags_any]': filters.tags.values.join(','),
            }),
          ...(filters.tags?.matchAll &&
            filters.tags?.values && {
              'q[client_tags]': filters.tags.values.join(','),
            }),
          ...(filters.customProfileField && {
            'q[client_profile_field]': `${filters.customProfileField.field}::${filters.customProfileField.value}`,
          }),
          ...(filters.appPlatform && {
            'q[app_platform]': filters.appPlatform,
          }),
        }),
      }),
    },
  });
};

export const exportHealthData = async ({
  clientIds,
  startDate,
  endDate,
  stats,
  profileFields,
  filters,
}: {
  clientIds?: string;
  startDate?: string;
  endDate?: string;
  stats?: string[];
  profileFields?: string[];
  filters?: Record<string, any>;
}) => {
  const params = {
    ...(clientIds && { with_ids: clientIds }),
    ...(startDate && { start_date: startDate }),
    ...(endDate && { end_date: endDate }),
    ...(stats?.length && {
      stats: stats.map((s) =>
        s
          .toLowerCase()
          .replace(/[\s_-]+/g, '_')
          .replace(/[\W]/g, '')
      ),
    }),
    ...(profileFields?.length && { profile_fields: profileFields }),
    ...filters,
  };

  return api.get('/api/v2/clients/export_health_data', { params });
};

export const fetchClientsByPluckColumns = async (
  pluckColumns: string
): Promise<Client[]> => {
  const response = await api.get('/api/v2/clients', {
    params: {
      fetch_all: true,
      pluck_columns: pluckColumns,
      'q[s]': 'client_name asc',
    },
  });

  return response.data.map((client: any) => normalizeClient(client));
};